import { useInfiniteQuery } from '@tanstack/react-query'
import { CARD_API_ENDPOINT } from '@/config/consts'
import { createSmartGet } from './useAuthenticatedMutation'

// Transaction type values from the enriched API
export enum TransactionType {
  SPEND = 'spend',
  WITHDRAW = 'withdraw',
  REFUND = 'refund',
  // Legacy values kept for backward compatibility
  PURCHASE = 'purchase',
  ADJUSTMENT = 'adjustment',
  FEE = 'fee',
}

export enum TransactionStatus {
  REQUESTED = 'requested',
  PENDING = 'pending',
  COMPLETED = 'completed',
  DECLINED = 'declined',
  REVERSED = 'reversed',
  REFUNDED = 'refunded',
  // Legacy values
  AUTHORIZED = 'authorized',
  POSTED = 'posted',
}

export type SettlementStatus = 'healthy' | 'processing' | 'attention'
export type TransactionFlow = 'debit' | 'refund' | 'declined'
export type OnchainTxStatus = 'pending' | 'processing' | 'completed' | 'failed'

// UserMessage mirrors swype-backend internal/usermessages.Message. The
// backend is the single source of truth for card-facing decline / error
// copy and fans the same value to the admin panel, this app, and Telegram
// notifications. Prefer `body` in UI; fall back to the raw `declined_reason`
// / `error_details` field only when this object is null.
export interface UserMessage {
  code: string
  name?: string
  title: string
  body: string
  /** Protocol-level description — admin / debug only. NEVER show to cardholders. */
  technical_message?: string
  severity: 'info' | 'warning' | 'error' | 'critical'
  suggested_action?:
    | ''
    | 'retry'
    | 'retry-later'
    | 'adjust-amount'
    | 'add-collateral'
    | 'reduce-borrow'
    | 'add-funds'
    | 'contact-support'
    | 'approve-token'
  recoverable?: boolean
  raw?: string
}

// Interfaces
export interface Merchant {
  name: string
  city?: string
  country?: string
  category?: string
  icon?: string
  // Legacy fields
  id?: string
  mcc?: string
}

export interface Transaction {
  id: string
  type: string // 'spend' | 'withdraw' | 'refund' (or legacy 'purchase' etc.)
  flow: TransactionFlow | string
  status: string
  amount: number // cents
  currency: string
  local_amount?: number // cents
  local_currency: string
  authorized_amount?: number // cents
  merchant: Merchant
  /**
   * Raw decline reason string (as recorded by Rain or our backend). Render
   * this only when `declined_message` is absent; otherwise prefer
   * `declined_message.body` which is the card-user-friendly copy.
   */
  declined_reason?: string
  declined_message?: UserMessage
  review_reason?: string
  requires_review?: boolean
  onchain_status?: OnchainTxStatus
  onchain_tx_hash?: string
  settlement_status: SettlementStatus
  created_at: string
  authorized_at?: string | null
  posted_at?: string | null
}

interface TransactionResponse {
  data: {
    transactions: Transaction[]
    pagination: {
      limit: number
      has_next: boolean
      next_cursor?: string
    }
  }
}

const fetchTransactions = async (cardID: string, cursor?: string): Promise<TransactionResponse> => {
  const params = new URLSearchParams()
  params.append('limit', '50')
  if (cursor) params.append('cursor', cursor)

  const url = `${CARD_API_ENDPOINT}/v1/vendor/cards/transactions/${cardID}?${params.toString()}`
  const getTransactions = createSmartGet(url)
  return getTransactions()
}

export function useTransactionHistory(cardID: string) {
  return useInfiniteQuery({
    queryKey: ['card-transactions', cardID],
    queryFn: ({ pageParam }) => fetchTransactions(cardID, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.data?.pagination?.has_next ? lastPage.data.pagination.next_cursor : undefined
    },
    enabled: !!cardID,
    refetchInterval: 1000 * 60 * 1,
  })
}
